import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Wrench,
  Plus,
  Search,
  Calendar,
  DollarSign,
  User,
  Phone,
  Mail,
  Star,
  Clock,
  AlertTriangle,
  CheckCircle,
  Users,
  Eye,
  X,
  Trash,
  Upload
} from 'lucide-react';
import { formatCurrency, formatDate, getStatusColor, getPriorityColor } from '../lib/utils';
import { useApi, useApiMutation } from '../hooks/useApi';
import { enhancedAPI, maintenanceAPI, contractorAPI } from '../services/api';
import AddMaintenanceRequestForm from '../components/forms/AddMaintenanceRequestForm';
import AddContractorForm from '../components/forms/AddContractorForm';
import ImportContractorsModal from '../components/ImportContractorsModal';
import MaintenanceEstimateApproval from '../components/MaintenanceEstimateApproval';

const Maintenance = () => {
  const [activeTab, setActiveTab] = useState('requests');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showAddRequest, setShowAddRequest] = useState(false);
  const [showAddContractor, setShowAddContractor] = useState(false);
  const [showImportContractors, setShowImportContractors] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showEstimateApproval, setShowEstimateApproval] = useState(false);
  const [selectedRequestForEstimate, setSelectedRequestForEstimate] = useState(null);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completionData, setCompletionData] = useState({
    completion_notes: '',
    contractor_rating: 5,
    work_quality: 5,
    timeliness: 5,
    communication: 5,
    overall_satisfaction: 5
  });

  const { data: requests, loading: loadingReq, error: errorReq, refetch: refetchReq } = useApi(() => maintenanceAPI.getAll());
  const { data: contractors, loading: loadingCon, error: errorCon, refetch: refetchCon } = useApi(() => contractorAPI.getAll());
  const { data: tenantsWithProps } = useApi(() => enhancedAPI.getTenantsWithProperties());
  const { mutate } = useApiMutation();

  const handleComplete = async () => {
    try {
      await maintenanceAPI.completeRequest(selectedRequest.id, completionData);
      setShowCompletionModal(false);
      setSelectedRequest(null);
      setCompletionData({
        completion_notes: '',
        contractor_rating: 5,
        work_quality: 5,
        timeliness: 5,
        communication: 5,
        overall_satisfaction: 5
      });
      refetchReq();
      refetchCon(); // Refresh contractor data to show updated ratings
    } catch (error) {
      console.error('Error completing maintenance request:', error);
      // TODO: Add proper error notification/toast
    }
  };

  const requestsWithDetails = useMemo(() => {
    if (!requests) return [];
    return requests.map(r => ({
      ...r,
      id: r.id || r._id,
      // Use enriched data from backend if available, otherwise fallback to lookup
      tenantName: r.tenant_name || 'Unknown Tenant',
      propertyName: r.property_name || 'Unknown Property',
      propertyAddress: r.property_address || 'Unknown Address',
      unitNumber: 'N/A', // This would need to be added to tenant data
      contractorName: r.contractor_name || null,
      contractorPhone: r.contractor_phone || null
    }));
  }, [requests]);

  const filteredRequests = requestsWithDetails.filter(request => {
    const matchesSearch = (request.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (request.tenantName || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === 'all' || request.status === filterStatus;
    const matchesCategory = filterCategory === 'all' || request.category === filterCategory;
    return matchesSearch && matchesFilter && matchesCategory;
  });
  
  // Get unique categories from requests
  const categories = useMemo(() => {
    const cats = new Set();
    requestsWithDetails.forEach(r => {
      if (r.category) cats.add(r.category);
    });
    return ['all', ...Array.from(cats)];
  }, [requestsWithDetails]);

  const stats = [
    {
      title: 'Total Requests',
      value: requestsWithDetails.length,
      icon: Wrench,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      title: 'Pending',
      value: requestsWithDetails.filter(r => r.status === 'pending').length,
      icon: Clock,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50'
    },
    {
      title: 'In Progress',
      value: requestsWithDetails.filter(r => r.status === 'in_progress').length,
      icon: AlertTriangle,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    },
    {
      title: 'Completed',
      value: requestsWithDetails.filter(r => r.status === 'completed').length,
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    }
  ];

  const RequestCard = ({ request }) => {
    const handleViewEstimate = () => {
      setSelectedRequestForEstimate(request.id);
      setShowEstimateApproval(true);
    };

    const needsEstimateApproval = request.status === 'pending_estimate_approval';
    const hasEstimate = request.estimated_cost && request.estimated_cost > 0;
    
    return (
      <Card className="hover:shadow-md transition-shadow">
        <CardContent className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{request.title}</h3>
              <p className="text-sm text-gray-600">{request.propertyName} - Unit {request.unitNumber}</p>
              <p className="text-sm text-gray-500">Submitted by: {request.tenantName}</p>
              {hasEstimate && (
                <p className="text-sm text-green-600 font-medium mt-1">
                  Estimated Cost: ${request.estimated_cost.toLocaleString()}
                </p>
              )}
            </div>
            <div className="flex flex-col space-y-2">
              <span className={`text-xs px-2 py-1 rounded ${getStatusColor(request.status)}`}>
                {request.status.replace('_', ' ')}
              </span>
              <span className={`text-xs px-2 py-1 rounded ${getPriorityColor(request.priority)}`}>
                {request.priority}
              </span>
              {needsEstimateApproval && (
                <span className="text-xs px-2 py-1 rounded bg-orange-100 text-orange-800">
                  Needs Approval
                </span>
              )}
            </div>
          </div>
          
          <p className="text-gray-700 mb-4">{request.description}</p>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="flex items-center space-x-2">
              <Calendar className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-600">
                Submitted: {formatDate(request.created_at)}
              </span>
            </div>
            {request.scheduled_date && (
              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-600">
                  Scheduled: {formatDate(request.scheduled_date)}
                </span>
              </div>
            )}
          </div>

          {/* Contractor Information */}
          {(request.contractor_name || request.contractor_id) && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <div className="flex items-center mb-2">
                <User className="h-4 w-4 text-blue-600 mr-2" />
                <span className="text-sm font-medium text-blue-800">Assigned Contractor</span>
              </div>
              <div className="space-y-1">
                {request.contractor_name && (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-blue-700 font-medium">{request.contractor_name}</span>
                  </div>
                )}
                {request.contractor_phone && (
                  <div className="flex items-center space-x-2">
                    <Phone className="h-3 w-3 text-blue-500" />
                    <span className="text-xs text-blue-600">{request.contractor_phone}</span>
                  </div>
                )}
                {request.contractor_email && (
                  <div className="flex items-center space-x-2">
                    <Mail className="h-3 w-3 text-blue-500" />
                    <span className="text-xs text-blue-600">{request.contractor_email}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {request.images && request.images.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Images:</p>
              <div className="flex space-x-2 flex-wrap">
                {request.images.map((image, index) => (
                  <img
                    key={index}
                    src={`${process.env.REACT_APP_BACKEND_URL}/uploads/${image}`}
                    alt={`Request ${index + 1}`}
                    className="w-16 h-16 object-cover rounded-lg border border-gray-200 hover:scale-105 transition-transform cursor-pointer"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      console.warn('Failed to load image:', image);
                    }}
                    onClick={() => {
                      // Open image in new tab
                      window.open(`${process.env.REACT_APP_BACKEND_URL}/uploads/${image}`, '_blank');
                    }}
                  />
                ))}
              </div>
            </div>
          )}
          
          <div className="flex justify-between items-center pt-4 border-t border-gray-200">
            <div className="text-sm text-gray-500">
              Category: <span className="capitalize font-medium">{request.category}</span>
            </div>
            <div className="flex space-x-2">
              <Button size="sm" variant="outline" onClick={() => { setSelectedRequest(request); setShowDetails(true); }}>
                <Eye className="h-4 w-4" />
              </Button>
              
              {needsEstimateApproval && (
                <Button
                  size="sm"
                  onClick={handleViewEstimate}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  <DollarSign className="h-4 w-4 mr-2" />
                  Review Estimate
                </Button>
              )}
              
              {hasEstimate && !needsEstimateApproval && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleViewEstimate}
                >
                  <DollarSign className="h-4 w-4 mr-2" />
                  View Estimate
                </Button>
              )}
              
              {!needsEstimateApproval && !request.contractor_name && request.status === 'pending' && (
                <Button
                  size="sm"
                  onClick={() => {
                    setSelectedRequest(request);
                    setShowAssignModal(true);
                  }}
                >
                  <Users className="h-4 w-4 mr-2" />
                  Assign Contractor
                </Button>
              )}
              
              {request.contractor_name && request.status !== 'completed' && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setSelectedRequest(request);
                    setShowAssignModal(true);
                  }}
                >
                  <Users className="h-4 w-4 mr-2" />
                  Change Contractor
                </Button>
              )}
              
              {(request.status === 'in_progress' || request.status === 'approved') && (
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => {
                    setSelectedRequest(request);
                    setShowCompletionModal(true);
                  }}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Mark Completed
                </Button>
              )}
              
              {/* Delete button - only show for pending requests WITHOUT assigned contractors */}
              {request.status === 'pending' && !request.contractor_id && !request.contractor_name && (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-600 hover:bg-red-50 border-red-300"
                  onClick={async () => {
                    if (window.confirm(`Are you sure you want to delete this work order?\n\n"${request.title}"\n\nThis action cannot be undone.`)) {
                      try {
                        await maintenanceAPI.delete(request.id);
                        refetchReq();
                      } catch (error) {
                        console.error('Error deleting work order:', error);
                        alert('Failed to delete work order. Please try again.');
                      }
                    }
                  }}
                >
                  <Trash className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Work Orders</h1>
          <p className="text-gray-600 mt-1">Track work orders and manage contractors</p>
        </div>
        <div className="flex space-x-3">
          <Button variant="outline" onClick={() => setShowImportContractors(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Import CSV
          </Button>
          <Button variant="outline" onClick={() => setShowAddContractor(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Contractor
          </Button>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setShowAddRequest(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Request
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                  </div>
                  <div className={`p-3 ${stat.bgColor} rounded-lg`}>
                    <Icon className={`h-6 w-6 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('requests')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'requests'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Wrench className="h-4 w-4 inline mr-2" />
            Work Orders
          </button>
          <button
            onClick={() => setActiveTab('contractors')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'contractors'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Users className="h-4 w-4 inline mr-2" />
            Contractors
          </button>
        </nav>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder={activeTab === 'requests' ? 'Search requests...' : 'Search contractors...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            {activeTab === 'requests' && (
              <>
                <div className="flex gap-2">
                  <Button
                    variant={filterStatus === 'all' ? 'default' : 'outline'}
                    onClick={() => setFilterStatus('all')}
                  >
                    All
                  </Button>
                  <Button
                    variant={filterStatus === 'pending' ? 'default' : 'outline'}
                    onClick={() => setFilterStatus('pending')}
                  >
                    Pending
                  </Button>
                  <Button
                    variant={filterStatus === 'in_progress' ? 'default' : 'outline'}
                    onClick={() => setFilterStatus('in_progress')}
                  >
                    In Progress
                  </Button>
                  <Button
                    variant={filterStatus === 'completed' ? 'default' : 'outline'}
                    onClick={() => setFilterStatus('completed')}
                  >
                    Completed
                  </Button>
                </div>
                <div className="flex items-center gap-2 min-w-[200px]">
                  <Label className="text-sm text-gray-600 whitespace-nowrap">Category:</Label>
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>
                        {cat === 'all' ? 'All Categories' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Content based on active tab */}
      {activeTab === 'requests' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredRequests.map((request) => (
            <RequestCard key={request.id} request={request} />
          ))}
        </div>
      )}

      {activeTab === 'contractors' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {contractors?.map((contractor) => (
            <ContractorCard key={contractor.id || contractor._id} contractor={contractor} />
          ))}
        </div>
      )}

      {/* Assign Contractor Modal */}
      {showAssignModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>Assign Contractor</CardTitle>
              <p className="text-sm text-gray-600">
                Work Order: {selectedRequest.title}
                <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                  Category: {selectedRequest.category || 'general'}
                </span>
              </p>
              <p className="text-xs text-gray-500 mt-2">
                💡 Contractors matching the work order category are highlighted in green
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {contractors
                ?.filter(contractor => contractor.available === true)  // Fix: use 'available' boolean instead of 'availability' string
                .sort((a, b) => {
                  // Sort matching contractors first
                  const aMatches = a.specialties?.some(s => s.toLowerCase() === selectedRequest.category?.toLowerCase());
                  const bMatches = b.specialties?.some(s => s.toLowerCase() === selectedRequest.category?.toLowerCase());
                  if (aMatches && !bMatches) return -1;
                  if (!aMatches && bMatches) return 1;
                  return 0;
                })
                .map((contractor) => {
                  const isMatch = contractor.specialties?.some(s => s.toLowerCase() === selectedRequest.category?.toLowerCase());
                  return (
                    <div 
                      key={contractor.id || contractor._id} 
                      className={`border rounded-lg p-4 transition-all ${
                        isMatch 
                          ? 'border-green-500 bg-green-50 hover:bg-green-100' 
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Avatar>
                            <AvatarImage src={contractor.profile_image} alt={contractor.name} />
                            <AvatarFallback>
                              {contractor.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium">{contractor.name}</h4>
                              {isMatch && (
                                <span className="px-2 py-0.5 bg-green-600 text-white text-xs rounded-full">
                                  ✓ Matches
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">{contractor.email}</p>
                            <div className="flex items-center space-x-2 mt-1">
                              <Star className="h-3 w-3 text-yellow-500" />
                              <span className="text-xs">{contractor.rating}</span>
                              <span className="text-xs text-gray-500">•</span>
                              <span className="text-xs">{contractor.specialties?.join(', ') || 'General'}</span>
                            </div>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          className={isMatch ? 'bg-green-600 hover:bg-green-700' : ''}
                          onClick={async () => {
                            try {
                              await maintenanceAPI.assignContractor(selectedRequest.id, contractor.id || contractor._id);
                              setShowAssignModal(false);
                              setSelectedRequest(null);
                              refetchReq();
                            } catch (error) {
                              console.error('Error assigning contractor:', error);
                              // TODO: Add proper error notification/toast
                            }
                          }}
                        >
                          Assign
                        </Button>
                      </div>
                    </div>
                  );
                })}

              {contractors?.filter(contractor => contractor.available === true).length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No available contractors found</p>
                  <p className="text-sm">Add contractors or check their availability status</p>
                </div>
              )}

              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowAssignModal(false)}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Request Details Modal */}
      {showDetails && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-3xl max-h-[85vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Request Details</CardTitle>
              <Button variant="ghost" onClick={() => { setShowDetails(false); setSelectedRequest(null); }}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold">{selectedRequest.title}</h3>
                <p className="text-sm text-gray-600">{selectedRequest.propertyName} - Unit {selectedRequest.unitNumber}</p>
                <p className="text-sm text-gray-500 mt-1">Status: {selectedRequest.status.replace('_',' ')}, Priority: {selectedRequest.priority}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium mb-2">Description</h4>
                  <p className="text-gray-700 whitespace-pre-wrap">{selectedRequest.description}</p>
                </div>
                <NotificationsPanel requestId={selectedRequest.id} />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add Request Form */}
      {showAddRequest && (
        <AddMaintenanceRequestForm
          onClose={() => setShowAddRequest(false)}
          onSuccess={() => {
            refetchReq();
            setShowAddRequest(false);
          }}
        />
      )}

      {/* Add Contractor Form */}
      {showAddContractor && (
        <AddContractorForm
          onClose={() => setShowAddContractor(false)}
          onSuccess={() => {
            refetchCon();
            setShowAddContractor(false);
          }}
        />
      )}

      {/* Estimate Approval Modal */}
      {showEstimateApproval && selectedRequestForEstimate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-lg shadow-xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Maintenance Estimate Approval</h2>
                <Button 
                  variant="ghost" 
                  onClick={() => {
                    setShowEstimateApproval(false);
                    setSelectedRequestForEstimate(null);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              <MaintenanceEstimateApproval
                requestId={selectedRequestForEstimate}
                onClose={() => {
                  setShowEstimateApproval(false);
                  setSelectedRequestForEstimate(null);
                }}
                onApproval={(estimateId, status) => {
                  refetchReq(); // Refresh maintenance requests to update status
                  if (status === 'approved') {
                    // Show success message or toast
                    console.log(`Estimate ${estimateId} approved`);
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}
      {/* Completion Modal */}
      {showCompletionModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span>Mark Request as Completed</span>
              </CardTitle>
              <p className="text-sm text-gray-600">Request: {selectedRequest.title}</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="completion_notes">Completion Notes</Label>
                <Textarea
                  id="completion_notes"
                  placeholder="Describe the work completed, any issues encountered, or additional notes..."
                  value={completionData.completion_notes}
                  onChange={(e) => setCompletionData(prev => ({
                    ...prev,
                    completion_notes: e.target.value
                  }))}
                  rows={4}
                />
              </div>

              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Rate the Contractor</h4>
                
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <Label htmlFor="contractor_rating">Overall Rating</Label>
                    <div className="flex items-center space-x-2 mt-1">
                      {[1, 2, 3, 4, 5].map((rating) => (
                        <button
                          key={rating}
                          type="button"
                          onClick={() => setCompletionData(prev => ({
                            ...prev,
                            contractor_rating: rating
                          }))}
                          className={`p-1 ${
                            rating <= completionData.contractor_rating
                              ? 'text-yellow-500'
                              : 'text-gray-300'
                          }`}
                        >
                          <Star className="h-5 w-5 fill-current" />
                        </button>
                      ))}
                      <span className="text-sm text-gray-600 ml-2">
                        {completionData.contractor_rating}/5
                      </span>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="work_quality">Work Quality</Label>
                    <div className="flex items-center space-x-2 mt-1">
                      {[1, 2, 3, 4, 5].map((rating) => (
                        <button
                          key={rating}
                          type="button"
                          onClick={() => setCompletionData(prev => ({
                            ...prev,
                            work_quality: rating
                          }))}
                          className={`p-1 ${
                            rating <= completionData.work_quality
                              ? 'text-yellow-500'
                              : 'text-gray-300'
                          }`}
                        >
                          <Star className="h-5 w-5 fill-current" />
                        </button>
                      ))}
                      <span className="text-sm text-gray-600 ml-2">
                        {completionData.work_quality}/5
                      </span>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="timeliness">Timeliness</Label>
                    <div className="flex items-center space-x-2 mt-1">
                      {[1, 2, 3, 4, 5].map((rating) => (
                        <button
                          key={rating}
                          type="button"
                          onClick={() => setCompletionData(prev => ({
                            ...prev,
                            timeliness: rating
                          }))}
                          className={`p-1 ${
                            rating <= completionData.timeliness
                              ? 'text-yellow-500'
                              : 'text-gray-300'
                          }`}
                        >
                          <Star className="h-5 w-5 fill-current" />
                        </button>
                      ))}
                      <span className="text-sm text-gray-600 ml-2">
                        {completionData.timeliness}/5
                      </span>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="communication">Communication</Label>
                    <div className="flex items-center space-x-2 mt-1">
                      {[1, 2, 3, 4, 5].map((rating) => (
                        <button
                          key={rating}
                          type="button"
                          onClick={() => setCompletionData(prev => ({
                            ...prev,
                            communication: rating
                          }))}
                          className={`p-1 ${
                            rating <= completionData.communication
                              ? 'text-yellow-500'
                              : 'text-gray-300'
                          }`}
                        >
                          <Star className="h-5 w-5 fill-current" />
                        </button>
                      ))}
                      <span className="text-sm text-gray-600 ml-2">
                        {completionData.communication}/5
                      </span>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="overall_satisfaction">Overall Satisfaction</Label>
                    <div className="flex items-center space-x-2 mt-1">
                      {[1, 2, 3, 4, 5].map((rating) => (
                        <button
                          key={rating}
                          type="button"
                          onClick={() => setCompletionData(prev => ({
                            ...prev,
                            overall_satisfaction: rating
                          }))}
                          className={`p-1 ${
                            rating <= completionData.overall_satisfaction
                              ? 'text-yellow-500'
                              : 'text-gray-300'
                          }`}
                        >
                          <Star className="h-5 w-5 fill-current" />
                        </button>
                      ))}
                      <span className="text-sm text-gray-600 ml-2">
                        {completionData.overall_satisfaction}/5
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowCompletionModal(false);
                    setSelectedRequest(null);
                    setCompletionData({
                      completion_notes: '',
                      contractor_rating: 5,
                      work_quality: 5,
                      timeliness: 5,
                      communication: 5,
                      overall_satisfaction: 5
                    });
                  }}
                >
                  Cancel
                </Button>
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={async () => {
                    try {
                      await maintenanceAPI.completeRequest(selectedRequest.id, completionData);
                      setShowCompletionModal(false);
                      setSelectedRequest(null);
                      setCompletionData({
                        completion_notes: '',
                        contractor_rating: 5,
                        work_quality: 5,
                        timeliness: 5,
                        communication: 5,
                        overall_satisfaction: 5
                      });
                      refetchReq();
                    } catch (error) {
                      console.error('Error completing request:', error);
                      // TODO: Add proper error notification/toast
                    }
                  }}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Complete Request
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Import Contractors Modal */}
      {showImportContractors && (
        <ImportContractorsModal
          onClose={() => setShowImportContractors(false)}
          onSuccess={() => {
            setShowImportContractors(false);
            refetchCon();
          }}
        />
      )}
    </div>
  );
};

const ContractorCard = ({ contractor }) => {
  const [showJobs, setShowJobs] = useState(false);
  const [completedJobs, setCompletedJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);

  const loadCompletedJobs = async () => {
    if (showJobs || !contractor.completed_jobs) return;
    
    setLoadingJobs(true);
    try {
      const jobs = await contractorAPI.getCompletedJobs(contractor.id || contractor._id);
      setCompletedJobs(jobs);
      setShowJobs(true);
    } catch (error) {
      console.error('Error loading completed jobs:', error);
    } finally {
      setLoadingJobs(false);
    }
  };

  const renderStarRating = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star 
        key={i} 
        className={`h-3 w-3 ${i < rating ? 'text-yellow-500 fill-current' : 'text-gray-300'}`} 
      />
    ));
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-start space-x-4">
          <Avatar className="h-16 w-16">
            <AvatarImage src={contractor.profile_image} alt={contractor.name} />
            <AvatarFallback>
              {contractor.name.split(' ').map(n => n[0]).join('').toUpperCase()}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">{contractor.name}</h3>
            <p className="text-sm text-gray-600 mb-2">{contractor.contact_person}</p>

            <div className="flex items-center space-x-4 mb-3">
              <div className="flex items-center space-x-1">
                <Star className="h-4 w-4 text-yellow-500" />
                <span className="text-sm font-medium">
                  {contractor.average_rating ? contractor.average_rating.toFixed(1) : contractor.rating || 'N/A'}
                </span>
                <span className="text-xs text-gray-500">
                  ({contractor.completed_jobs || 0} jobs)
                </span>
              </div>
              <div className="flex items-center space-x-1">
                <DollarSign className="h-4 w-4 text-gray-400" />
                <span className="text-sm">{formatCurrency(contractor.hourly_rate || 0)}/hr</span>
              </div>
              <span className={`text-xs px-2 py-1 rounded ${contractor.availability === 'available' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {contractor.availability || 'unknown'}
              </span>
            </div>

            {/* Enhanced ratings breakdown */}
            {contractor.average_rating && (
              <div className="grid grid-cols-2 gap-2 mb-3 p-2 bg-gray-50 rounded">
                <div className="text-xs">
                  <span className="text-gray-600">Quality: </span>
                  <span className="font-medium">{contractor.average_work_quality?.toFixed(1) || 'N/A'}</span>
                </div>
                <div className="text-xs">
                  <span className="text-gray-600">Timeliness: </span>
                  <span className="font-medium">{contractor.average_timeliness?.toFixed(1) || 'N/A'}</span>
                </div>
                <div className="text-xs">
                  <span className="text-gray-600">Communication: </span>
                  <span className="font-medium">{contractor.average_communication?.toFixed(1) || 'N/A'}</span>
                </div>
                <div className="text-xs">
                  <span className="text-gray-600">Satisfaction: </span>
                  <span className="font-medium">{contractor.average_satisfaction?.toFixed(1) || 'N/A'}</span>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 mb-3">
              <div className="flex items-center space-x-2">
                <Phone className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-600">{contractor.phone}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Mail className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-600">{contractor.email}</span>
              </div>
            </div>

            <div className="mb-3">
              <p className="text-sm text-gray-600 mb-1">Specialties:</p>
              <div className="flex flex-wrap gap-1">
                {(contractor.specialties || []).map((specialty, index) => (
                  <span key={index} className="text-xs px-2 py-1 border rounded">
                    {specialty.replace('_', ' ')}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-500">
                Completed: {contractor.completed_jobs || 0} jobs
              </span>
              {contractor.last_job_completed && (
                <span className="text-gray-500">
                  Last worked: {formatDate(contractor.last_job_completed)}
                </span>
              )}
              {(contractor.completed_jobs > 0) && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={loadCompletedJobs}
                  disabled={loadingJobs}
                >
                  {loadingJobs ? 'Loading...' : showJobs ? 'Hide Jobs' : 'View Jobs'}
                </Button>
              )}
            </div>

            {/* Completed Jobs List */}
            {showJobs && completedJobs.length > 0 && (
              <div className="mt-4 border-t pt-4">
                <h4 className="text-sm font-medium mb-3">Recent Completed Jobs</h4>
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {completedJobs.slice(0, 5).map((job) => (
                    <div key={job.job_id} className="border rounded p-3 bg-white">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="text-sm font-medium">{job.title}</p>
                          <p className="text-xs text-gray-600">{job.property_name}</p>
                        </div>
                        <div className="flex items-center space-x-1">
                          {renderStarRating(job.contractor_rating)}
                          <span className="text-xs ml-1">{job.contractor_rating}/5</span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 mb-2">{job.description}</p>
                      {job.completion_notes && (
                        <p className="text-xs text-green-700 bg-green-50 p-1 rounded">
                          "{job.completion_notes}"
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        Completed: {formatDate(job.completed_at)}
                      </p>
                    </div>
                  ))}
                  {completedJobs.length > 5 && (
                    <p className="text-xs text-gray-500 text-center">
                      ... and {completedJobs.length - 5} more jobs
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const NotificationsPanel = ({ requestId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resendingId, setResendingId] = useState('');

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await maintenanceAPI.getNotifications(requestId);
        if (active) setItems(res.notifications || []);
      } catch (e) {
        if (active) setError(e.message || 'Failed to load notifications');
      } finally {
        if (active) setLoading(false);
      }
    };
    if (requestId) load();
    return () => { active = false; };
  }, [requestId]);

  return (
    <div>
      <h4 className="font-medium mb-2">Notifications</h4>
      {loading && <div className="text-sm text-gray-500">Loading...</div>}
      {error && <div className="text-sm text-red-600">{error}</div>}
      {!loading && (!items || items.length === 0) && <div className="text-sm text-gray-500">No notifications yet.</div>}
      {items && items.length > 0 && (
        <div className="space-y-2">
          {items.map(n => (
            <div key={n._id || n.id} className="p-3 border rounded-md">
              <div className="text-sm text-gray-700"><span className="capitalize">{n.event}</span> via {n.channel.toUpperCase()}</div>
              <div className="text-xs text-gray-500">To: {n.to}</div>
              <div className="text-xs text-gray-500">{new Date(n.created_at).toLocaleString()}</div>
              <div className="text-xs text-gray-600 mt-1">{n.body_preview}</div>
              <div className="text-xs mt-1">Status: <span className="capitalize">{n.status}</span></div>
              {n.status !== 'sent' && (
                <div className="mt-2">
                  <Button size="sm" variant="outline" disabled={resendingId === (n._id || n.id)} onClick={async ()=>{
                    setResendingId(n._id || n.id);
                    try {
                      await maintenanceAPI.resendNotification(n._id || n.id);
                      // reload list
                      const res = await maintenanceAPI.getNotifications(requestId);
                      setItems(res.notifications || []);
                    } catch (e) {
                      // could add toast here
                    } finally {
                      setResendingId('');
                    }
                  }}>Resend SMS</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

    </div>
  );
};

export default Maintenance;